import { useEffect, useMemo, useState } from 'react';
import { Box, Button, Container, IconButton, Stack, Typography } from '@mui/material';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import HeroAboutPanel from './HeroAboutPanel';
import HeroContactPanel from './HeroContactPanel';
import HeroRegistrationPanel from './HeroRegistrationPanel';

export default function HeroSlider({
  slides = [],
  aboutConfig = {},
  whatsappHref = '',
  fallbackTitle,
  fallbackSubtitle,
  fallbackButtonText,
  fallbackButtonLink
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const aboutOpen = location.hash === '#quienes-somos';
  const contactOpen = location.hash === '#contactanos';
  const registrationOpen = location.hash === '#registro';
  const panelOpen = aboutOpen || contactOpen || registrationOpen;
  useEffect(() => {
    if (panelOpen) window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [panelOpen]);
  const closeAbout = () => navigate({ pathname: '/', hash: '' }, { replace: true });
  const validSlides = useMemo(() => {
    return Array.isArray(slides) ? slides.filter((s) => s?.imagen_url) : [];
  }, [slides]);

  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (!validSlides.length) {
      setIndex(0);
      return;
    }

    setIndex((prev) => {
      if (prev >= validSlides.length) return 0;
      return prev;
    });
  }, [validSlides.length]);

  const slide = validSlides[index] || null;

  const image = slide?.imagen_url || '';

  const title = slide?.titulo || fallbackTitle || 'Fresh Ecuadorian Flowers';

  const subtitle =
    slide?.subtitulo ||
    fallbackSubtitle ||
    'Premium flowers selected with care for international markets.';

  const buttonText = slide?.boton_texto || fallbackButtonText || 'View catalog';

  const buttonLink = slide?.boton_link || fallbackButtonLink || '/catalogo';

  const durationMs = Math.max(2500, Number(slide?.duracion_segundos || 5) * 1000);

  const hasManySlides = validSlides.length > 1;

  useEffect(() => {
    if (!hasManySlides || panelOpen) return;

    const timer = setTimeout(() => {
      setIndex((prev) => (prev + 1) % validSlides.length);
    }, durationMs);

    return () => clearTimeout(timer);
  }, [hasManySlides, validSlides.length, durationMs, index, panelOpen]);

  const goToSlide = (nextIndex) => {
    if (!validSlides.length) return;

    const total = validSlides.length;
    const safeIndex = ((nextIndex % total) + total) % total;
    setIndex(safeIndex);
  };

  const isExternalLink = /^https?:\/\//i.test(buttonLink);

  const mainButtonProps = isExternalLink
    ? {
        component: 'a',
        href: buttonLink,
        target: '_blank',
        rel: 'noopener noreferrer'
      }
    : {
        component: RouterLink,
        to: buttonLink
      };

  return (
    <Box
      component="section"
      sx={{
        position: 'relative',
        minHeight: '100vh',
        '@supports (min-height: 100svh)': { minHeight: '100svh' },
        width: '100%',
        boxSizing: 'border-box',
        pt: { xs: '172px', sm: '156px', md: '140px' },
        pb: { xs: '96px', sm: '100px', md: '112px' },
        ...(panelOpen && {
          pt: 'calc(var(--public-header-height, 172px) + 24px)',
          pb: '40px'
        }),
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
        color: '#fff',
        bgcolor: '#07110c',
        isolation: 'isolate',
        '@keyframes heroFadeIn': {
          from: {
            opacity: 0,
            transform: 'scale(1.04)'
          },
          to: {
            opacity: 1,
            transform: 'scale(1)'
          }
        },
        '@keyframes heroProgress': {
          from: {
            width: '0%'
          },
          to: {
            width: '100%'
          }
        }
      }}
    >
      <Box
        key={image || `fallback-${index}`}
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: -3,
          backgroundImage: image ? `url(${image})` : 'linear-gradient(135deg, #0c1b12, #1f3529)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          animation: 'heroFadeIn 900ms ease both',
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' }
        }}
      />

      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: -2,
          background:
            'linear-gradient(90deg, rgba(4,12,8,.88) 0%, rgba(4,12,8,.62) 44%, rgba(4,12,8,.22) 100%)'
        }}
      />

      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: -1,
          background:
            'radial-gradient(circle at 20% 30%, rgba(255,255,255,.15), transparent 28%), radial-gradient(circle at 80% 70%, rgba(216,177,106,.18), transparent 30%)'
        }}
      />

      {hasManySlides && (
        <Box
          key={`${index}-${durationMs}`}
          sx={{
            position: 'absolute',
            left: 0,
            bottom: 0,
            height: 4,
            bgcolor: '#d8b16a',
            animation: `heroProgress ${durationMs}ms linear forwards`,
            animationPlayState: panelOpen ? 'paused' : 'running',
            zIndex: 3
          }}
        />
      )}

      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2, display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: panelOpen ? 'minmax(0, 1fr) minmax(0, 1.15fr)' : '1fr' },
        alignItems: 'center', gap: { xs: 3, md: 6 } }}>
        <Stack
          spacing={{ xs: 2.5, md: 3 }}
          sx={{
            width: '100%',
            display: { xs: panelOpen ? 'none' : 'flex', md: 'flex' },
            maxWidth: { xs: '100%', md: 760 },
            py: { xs: 2, md: 3 },
            '@media (min-width: 900px) and (max-height: 800px)': {
              gap: 2,
              py: 0,
              '& > :not(style) ~ :not(style)': { marginTop: 0 }
            }
          }}
        >
          <Box
            sx={{
              width: 'fit-content',
              px: 2,
              py: 0.8,
              border: '1px solid rgba(255,255,255,.28)',
              borderRadius: 999,
              bgcolor: 'rgba(255,255,255,.10)',
              backdropFilter: 'blur(12px)'
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: 11, sm: 13 },
                letterSpacing: 2.2,
                textTransform: 'uppercase',
                fontWeight: 800
              }}
            >
              Fresh Exports · Ecuador
            </Typography>
          </Box>

          <Stack spacing={2}>
            <Typography
              component="h1"
              sx={{
                fontWeight: 900,
                lineHeight: 0.95,
                letterSpacing: { xs: -1.2, md: -2.5 },
                fontSize: {
                  xs: 'clamp(2rem, 9vw, 2.75rem)',
                  sm: 'clamp(2.75rem, 7vw, 3.625rem)',
                  md: 'clamp(3rem, 9vh, 5.125rem)',
                  lg: 'clamp(3.25rem, 9vh, 5.875rem)'
                },
                display: 'grid',
                overflowWrap: 'anywhere',
                maxWidth: 920,
                textShadow: '0 18px 60px rgba(0,0,0,.42)'
              }}
            >
              {/* Reservar el mayor texto mantiene la misma altura entre diapositivas. */}
              {validSlides.map((item, i) => (
                <Box
                  component="span"
                  key={item.id || i}
                  aria-hidden="true"
                  sx={{ gridArea: '1 / 1', visibility: 'hidden' }}
                >
                  {item.titulo || fallbackTitle || 'Fresh Ecuadorian Flowers'}
                </Box>
              ))}
              <Box component="span" sx={{ gridArea: '1 / 1' }}>{title}</Box>
            </Typography>

            {!!subtitle && (
              <Typography
                sx={{
                  maxWidth: 680,
                  display: 'grid',
                  overflowWrap: 'anywhere',
                  fontSize: { xs: 16, md: 'clamp(1rem, 2.4vh, 1.3125rem)' },
                  lineHeight: 1.5,
                  color: 'rgba(255,255,255,.88)',
                  fontWeight: 400
                }}
              >
                {validSlides.map((item, i) => (
                  <Box
                    component="span"
                    key={item.id || i}
                    aria-hidden="true"
                    sx={{ gridArea: '1 / 1', visibility: 'hidden' }}
                  >
                    {item.subtitulo || fallbackSubtitle ||
                      'Premium flowers selected with care for international markets.'}
                  </Box>
                ))}
                <Box component="span" sx={{ gridArea: '1 / 1' }}>{subtitle}</Box>
              </Typography>
            )}
          </Stack>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', sm: 'center' }}
          >
            <Button
              {...mainButtonProps}
              variant="contained"
              size="large"
              sx={{
                width: { xs: '100%', sm: 'fit-content' },
                px: 4,
                py: 1.55,
                borderRadius: 999,
                bgcolor: '#fff',
                color: '#101610',
                fontWeight: 900,
                boxShadow: '0 18px 45px rgba(0,0,0,.28)',
                textTransform: 'none',
                fontSize: 16,
                '&:hover': {
                  bgcolor: '#f4efe5',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 24px 55px rgba(0,0,0,.34)'
                },
                transition: 'all .25s ease'
              }}
            >
              {buttonText}
            </Button>

            <Button
              component={RouterLink}
              to="/catalogo"
              variant="outlined"
              size="large"
              sx={{
                width: { xs: '100%', sm: 'fit-content' },
                px: 4,
                py: 1.55,
                borderRadius: 999,
                color: '#fff',
                borderColor: 'rgba(255,255,255,.55)',
                fontWeight: 800,
                textTransform: 'none',
                fontSize: 16,
                backdropFilter: 'blur(10px)',
                bgcolor: 'rgba(255,255,255,.06)',
                '&:hover': {
                  borderColor: '#fff',
                  bgcolor: 'rgba(255,255,255,.14)',
                  transform: 'translateY(-2px)'
                },
                transition: 'all .25s ease'
              }}
            >
              Explore varieties
            </Button>
          </Stack>

          <Stack
            direction="row"
            spacing={{ xs: 2, md: 4 }}
            sx={{
              pt: { xs: 1, md: 2 },
              flexWrap: 'wrap',
              rowGap: 2
            }}
          >
            {[
              ['Export Quality', 'Premium selection'],
              ['Cold Chain', 'Guaranteed freshness'],
              ['Worldwide', 'Worldwide shipping']
            ].map(([top, bottom]) => (
              <Box
                key={top}
                sx={{
                  pr: { xs: 2, md: 4 },
                  borderRight: {
                    xs: 'none',
                    sm: '1px solid rgba(255,255,255,.22)'
                  },
                  '&:last-of-type': {
                    borderRight: 'none'
                  }
                }}
              >
                <Typography sx={{ fontWeight: 900, fontSize: 16 }}>{top}</Typography>

                <Typography
                  sx={{
                    color: 'rgba(255,255,255,.68)',
                    fontSize: 13
                  }}
                >
                  {bottom}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Stack>
        {aboutOpen && (
          <HeroAboutPanel config={aboutConfig} whatsappHref={whatsappHref} onClose={closeAbout} />
        )}
        {contactOpen && (
          <HeroContactPanel config={aboutConfig} onClose={closeAbout} />
        )}
        {registrationOpen && (
          <HeroRegistrationPanel onClose={closeAbout} />
        )}
      </Container>

        {hasManySlides && !panelOpen && (
          <>
            <Stack
              direction="row"
              spacing={1}
              sx={{
                position: 'absolute',
                left: { xs: 24, md: '6%' },
                bottom: { xs: 38, md: 44 },
                maxWidth: 'calc(100% - 160px)',
                flexWrap: 'wrap',
                rowGap: 1
              }}
            >
              {validSlides.map((item, i) => (
                <Box
                  key={item.id || item.imagen_url || i}
                  component="button"
                  type="button"
                  aria-label={`Go to slide ${i + 1}`}
                  aria-current={i === index ? 'true' : undefined}
                  onClick={() => goToSlide(i)}
                  sx={{
                    width: i === index ? 34 : 10,
                    height: 10,
                    borderRadius: 999,
                    border: 0,
                    p: 0,
                    cursor: 'pointer',
                    bgcolor: i === index ? '#fff' : 'rgba(255,255,255,.42)',
                    transition: 'all .25s ease'
                  }}
                />
              ))}
            </Stack>

            <Stack
              direction="row"
              spacing={1}
              sx={{
                position: 'absolute',
                right: { xs: 16, md: '6%' },
                bottom: { xs: 24, md: 40 }
              }}
            >
              <IconButton
                aria-label="Previous slide"
                onClick={() => goToSlide(index - 1)}
                sx={{
                  width: 46,
                  height: 46,
                  color: '#fff',
                  border: '1px solid rgba(255,255,255,.34)',
                  bgcolor: 'rgba(255,255,255,.10)',
                  backdropFilter: 'blur(12px)',
                  fontSize: 34,
                  lineHeight: 1,
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,.22)'
                  }
                }}
              >
                ‹
              </IconButton>

              <IconButton
                aria-label="Next slide"
                onClick={() => goToSlide(index + 1)}
                sx={{
                  width: 46,
                  height: 46,
                  color: '#fff',
                  border: '1px solid rgba(255,255,255,.34)',
                  bgcolor: 'rgba(255,255,255,.10)',
                  backdropFilter: 'blur(12px)',
                  fontSize: 34,
                  lineHeight: 1,
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,.22)'
                  }
                }}
              >
                ›
              </IconButton>
            </Stack>
          </>
        )}
    </Box>
  );
}
